'use client'
import { usePathname, useRouter } from 'next/navigation'
import { useState, useCallback, useEffect, useMemo } from 'react'

import ShareBottomSheet from '@/components/detail/components/shared/parkinglotShare/view'
import CommentSectionView from '@/components/map/components/summary/components/commentSection/view'
import FunctionSectionView from '@/components/map/components/summary/components/functionSection/view'
import InfoSectionView from '@/components/map/components/summary/components/infoSection/view'
import TitleSectionView from '@/components/map/components/summary/components/titleSection/view'

import exploreEvents from '@/utils/gaEvents/exploreEvents'

import type { ParkinglotDetail, TicketsItem } from '@/api/server/detail/dto/detailDto'
import Shared from '@/shared'
import NavigationView from '@/shared/components/navigation/view'
import SectionBar from '@/shared/components/SectionBar'
import useHash from '@/shared/hooks/useHash'

import { PARKINGLOT_TYPE, SHEET_STATE_TYPE } from '@/enums/detail'
import { CATE_CATEGORY } from '@/enums/gtagEvents'
import { HASH_KEY_TYPE } from '@/enums/map'

export interface ParkinglotSummaryProps {
  className?: React.ComponentProps<'div'>['className']
  pin: ParkinglotDetail
  tickets: TicketsItem[]
  isRoadViewOpen: boolean
  setIsRoadViewOpen: React.Dispatch<React.SetStateAction<boolean>>
  sheetType: SHEET_STATE_TYPE
}

export default function ParkinglotSummary(props: ParkinglotSummaryProps) {
  const { className, pin, tickets, setIsRoadViewOpen, sheetType } = props
  const router = useRouter()
  const pathname = usePathname()
  const { updateHash, getHashByKey } = useHash()
  const [isNavigationOpen, setIsNavigationOpen] = useState(false)
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isCopySuccess, setIsCopySuccess] = useState(false)
  const [isCopyFail, setIsCopyFail] = useState(false)

  const isTickets = useMemo(() => tickets.length > 0, [tickets])
  const customClassName = className ? className : 'bottom-0 left-0 flex w-full'

  const removeWordOperationTime = useMemo(() => {
    if (pin.openFree.operationTime == null) return ''
    const regex = /익일\s*/g
    return pin.openFree.operationTime.replace(regex, '')
  }, [pin.openFree.operationTime])

  const copyAddress = useCallback(async () => {
    try {
      await Shared.Utils.handleCopyClipBoard(pin.basic.newAddress || pin.basic.address)
      setIsCopySuccess(true)
    } catch (err) {
      setIsCopyFail(true)
    }
  }, [pin.basic.newAddress, pin.basic.address])

  useEffect(() => {
    if (pin.type === PARKINGLOT_TYPE.PARKINGLOT && sheetType === SHEET_STATE_TYPE.VISIBLE) {
      exploreEvents.partner.onLoadParkinglotDetail(
        pin.basic.partnerStatus ? CATE_CATEGORY.PARTNER : CATE_CATEGORY.NORMAL,
        pin.seq,
        pin.basic.latitude,
        pin.basic.longitude
      )
    }
  }, [pin, sheetType])

  const onClickSwipeButton = () => {
    const pathname = window.location.pathname
    const searchParams = window.location.search
    const hash = updateHash({
      sheet: sheetType === SHEET_STATE_TYPE.VISIBLE ? SHEET_STATE_TYPE.HIDDEN : SHEET_STATE_TYPE.VISIBLE
    })
    router.push(`${pathname}${searchParams}${hash}`)
  }

  const isFullSheet =
    getHashByKey(HASH_KEY_TYPE.SHEET) === SHEET_STATE_TYPE.VISIBLE ||
    pathname.startsWith(`/${PARKINGLOT_TYPE.PARKINGLOT.toLowerCase()}`)

  return (
    <div className={`${customClassName}`}>
      <div className={`relative mx-[0] mb-[0] mt-[0] w-full`}>
        <div
          className="absolute left-1/2 flex -translate-x-1/2 cursor-pointer justify-center px-2 py-[10px]"
          onClick={onClickSwipeButton}
        >
          <div className="h-1 w-10 rounded-[26px] bg-020"></div>
        </div>
        <div className={`flex w-full flex-col pt-[24px]`}>
          <TitleSectionView
            parkinglotName={pin.basic.name}
            parkinglotType={pin.type}
            isPartner={pin.basic.partnerStatus}
            category={pin.basic.category}
            qty={pin.basic.qty}
          />
          <div
            className="grid"
            style={{ gridTemplateRows: sheetType === SHEET_STATE_TYPE.VISIBLE || !isTickets ? '1fr' : '0fr' }}
          >
            <div className="overflow-hidden">
              <InfoSectionView
                parkinglotType={pin.type}
                isCopySuccess={isCopySuccess}
                setIsCopySuccess={setIsCopySuccess}
                isCopyFail={isCopyFail}
                setIsCopyFail={setIsCopyFail}
                copyAddress={copyAddress}
                address={pin.basic.newAddress || pin.basic.address}
                calcPrice={pin.basic.calcPrices && pin.basic.calcPrices['60']}
                operationTime={removeWordOperationTime}
                isFullSheet={isFullSheet}
              />

              <CommentSectionView
                isPartner={pin.basic.partnerStatus}
                moduComment={pin.basic.moduComment}
                tickets={tickets}
              />

              <SectionBar className="mt-[20px]" border="border-t-[1px]" />
              <FunctionSectionView
                pin={pin}
                setIsShareOpen={setIsShareOpen}
                setIsNavigationOpen={setIsNavigationOpen}
                setIsRoadViewOpen={setIsRoadViewOpen}
              />
              <SectionBar className="mb-6" border="border-t-[10px]" />
            </div>
          </div>
        </div>

        <NavigationView
          targetId={sheetType === SHEET_STATE_TYPE.VISIBLE ? '#layer2' : '#layer1'}
          isOpen={isNavigationOpen}
          onClose={() => setIsNavigationOpen(false)}
          parkinglotName={pin.basic.name}
          latitude={pin.basic.latitude}
          longitude={pin.basic.longitude}
        />
        <ShareBottomSheet
          targetId={sheetType === SHEET_STATE_TYPE.VISIBLE ? '#layer2' : '#layer1'}
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          type={pin.type}
          seq={pin.seq}
          parkinglotName={pin.basic.name}
          parkinglotAddress={pin.basic.newAddress || pin.basic.address}
        />
      </div>
    </div>
  )
}
